import { ShiftConfig } from '../hooks/useUserShift';

export type PulangStatus = 'pulang_cepat' | 'tepat_waktu' | 'lembur' | 'tidak_presensi_pulang';
export type MasukStatus = 'tepat_waktu' | 'terlambat';

// Zona kerja resmi lembaga: WIB (UTC+7).
const WIB_OFFSET_MS = 7 * 60 * 60 * 1000;

/**
 * Toleransi presensi pulang: presensi mulai 1 menit sebelum jam pulang
 * (mis. 15:59:00 untuk jam pulang 16:00) sudah dihitung tepat waktu.
 * Tanpa ini, presensi "tepat 16:00" di HP yang jamnya tertinggal beberapa
 * detik tercatat pulang cepat.
 */
export const TOLERANSI_PULANG_MS = 60 * 1000;

/**
 * Tanggal "hari presensi" dalam zona WIB (YYYY-MM-DD), TIDAK memakai jam/zona
 * perangkat. Dipakai untuk cek duplikat di client supaya record shift kemarin
 * (yang ikut termuat via lookback sesi aktif) TIDAK memblokir presensi masuk
 * hari ini.
 */
export const getWibDateString = (instant: Date): string => {
    const wib = new Date(instant.getTime() + WIB_OFFSET_MS);
    return `${wib.getUTCFullYear()}-${String(wib.getUTCMonth() + 1).padStart(2, '0')}-${String(wib.getUTCDate()).padStart(2, '0')}`;
};

export interface PresensiLogicResult {
    status: 'success' | 'error' | 'early_masuk' | 'late_masuk' | 'overlap_pulang' | 'early_pulang' | 'late_pulang';
    message?: string;
    determinedStatus?: PulangStatus | MasukStatus;
}

/**
 * Jam akhir toleransi masuk hari ini (jam mulai shift + toleransi), mis. shift
 * 06:50 dengan toleransi 10 menit → 07:00. Presensi sampai jam ini masih tepat waktu.
 */
export const getJamAkhirToleransi = (currentConfig: ShiftConfig, now: Date = new Date()): Date => {
    const [startHour, startMinute] = currentConfig.start.split(':').map(Number);
    const shiftStart = new Date(now);
    shiftStart.setHours(startHour, startMinute, 0, 0);
    const TOLERANCE_MINUTES = currentConfig.tolerance || 15;
    return new Date(shiftStart.getTime() + TOLERANCE_MINUTES * 60000);
};

/** Udzur "Lupa" / "Aplikasi Error" hanya berlaku bila terlambat kurang dari ini. */
export const BATAS_UDZUR_MENIT = 120;

export type UdzurMasuk = 'lupa' | 'aplikasi_error';

export const UDZUR_LABEL: Record<UdzurMasuk, string> = {
    lupa: 'Lupa',
    aplikasi_error: 'Aplikasi Error',
};

/** Apakah keterlambatan masih dalam batas udzur (kurang dari 2 jam setelah akhir toleransi). */
export const isDalamBatasUdzur = (jamAkhirToleransi: Date, now: Date = new Date()): boolean =>
    now.getTime() - jamAkhirToleransi.getTime() < BATAS_UDZUR_MENIT * 60000;

/** Masa penyesuaian (saklar global) sedang berlaku hari ini (WIB)? `sampai` = hari terakhir, YYYY-MM-DD. */
export const isMasaPenyesuaianBerlaku = (m: { aktif: boolean; sampai: string | null } | null, now: Date = new Date()) =>
    Boolean(m?.aktif && (!m.sampai || getWibDateString(now) <= m.sampai));

export const checkPresensiMasuk = (
    currentConfig: ShiftConfig,
    now: Date = new Date()
): PresensiLogicResult => {
    const [startHour, startMinute] = currentConfig.start.split(':').map(Number);
    const shiftStart = new Date(now);
    shiftStart.setHours(startHour, startMinute, 0, 0);

    const PRESENSI_AWAL_MINUTES = currentConfig.presensiAwal || 60;

    const earlyWindow = new Date(shiftStart.getTime() - PRESENSI_AWAL_MINUTES * 60000);
    const toleranceWindow = getJamAkhirToleransi(currentConfig, now);

    if (now < earlyWindow) {
        return {
            status: 'early_masuk',
            message: `Belum waktunya presensi ${currentConfig.name || ''}. Presensi dibuka pukul ${earlyWindow.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.`
        };
    }

    if (now <= toleranceWindow) {
        return { status: 'success', determinedStatus: 'tepat_waktu' };
    }

    return { status: 'late_masuk', determinedStatus: 'terlambat' };
};

export const checkPresensiPulang = (
    currentConfig: ShiftConfig,
    activeShifts: ShiftConfig[],
    now: Date = new Date(),
    jamMasukStr?: string
): PresensiLogicResult => {
    const [startHour, startMinute] = currentConfig.start.split(':').map(Number);
    const [endHour, endMinute] = currentConfig.end.split(':').map(Number);

    // If we have the original jamMasuk, use its date as the base for the shift start/end
    // This is crucial for evaluating sessions that cross day boundaries.
    const baseDate = jamMasukStr ? new Date(jamMasukStr) : new Date(now);

    const shiftStart = new Date(baseDate);
    shiftStart.setHours(startHour, startMinute, 0, 0);

    const shiftEnd = new Date(baseDate);
    shiftEnd.setHours(endHour, endMinute, 0, 0);

    // Handle night shifts that end on the next day
    if (shiftEnd < shiftStart) {
        shiftEnd.setDate(shiftEnd.getDate() + 1);
    }

    const PRESENSI_AKHIR_HOURS = currentConfig.presensiAkhir || 2;
    const lateLimit = new Date(shiftEnd.getTime() + PRESENSI_AKHIR_HOURS * 60 * 60 * 1000);
    const maxLemburLimit = new Date(lateLimit.getTime() + 2 * 60 * 60 * 1000); // Total 2 hours tolerance for Lembur

    // Check for Overlap with Next Shift
    let isOverlap = false;
    if (activeShifts && activeShifts.length > 1) {
        for (const shift of activeShifts) {
            if (shift.name === currentConfig.name) continue;

            const [sHour, sMinute] = shift.start.split(':').map(Number);
            const sTime = new Date(now);
            sTime.setHours(sHour, sMinute, 0, 0);

            // Only consider overlapping if the other shift is a subsequent shift
            if (sTime > shiftStart && now >= sTime) {
                isOverlap = true;
                break;
            }
        }
    }

    if (isOverlap) {
        return { status: 'overlap_pulang', determinedStatus: 'tidak_presensi_pulang' };
    }

    if (now.getTime() < shiftEnd.getTime() - TOLERANSI_PULANG_MS) {
        return { status: 'early_pulang', determinedStatus: 'pulang_cepat' };
    }

    if (now <= lateLimit) {
        return { status: 'success', determinedStatus: 'tepat_waktu' };
    }

    if (now <= maxLemburLimit) {
        return { status: 'success', determinedStatus: 'lembur' };
    }

    return { status: 'late_pulang', determinedStatus: 'tidak_presensi_pulang' };
};